<!-- frontend/src/components/framework-gen-ai/ProductSpecGenerator.vue
     eQuote AI — Product Spec Generator
     Paste a simplified SKU description, match it against the official
     All-In-One Product Spec workbook, and get the full spec (Markdown) plus a
     downloadable formatted Excel sheet.
     Backend (deterministic, no LLM):
       POST /genova-graph/api/product-spec/generate/
       POST /genova-graph/api/product-spec/excel/
-->
<template>
  <div v-if="!prdEnv()" class="spec-gen">
    <div class="spec-header">
      <h2>eQuote AI · 產品規格產生器</h2>
      <p class="spec-sub">
        貼上精簡的 SKU 描述，自動比對官方產品規格總表，列出完整規格並可下載 Excel 正式規格表。
      </p>
    </div>

    <!-- One-click example prompts -->
    <div class="spec-examples">
      <span class="ex-title">試試範例：</span>
      <button
        v-for="(ex, i) in examples"
        :key="i"
        class="ex-card"
        :disabled="loading"
        :title="ex.prompt"
        @click="runExample(ex)">
        <span class="ex-tag" :class="ex.kind">{{ ex.tag }}</span>
        <span class="ex-label">{{ ex.label }}</span>
      </button>
    </div>

    <div class="spec-input-card">
      <label class="spec-label">SKU Description / 使用情境</label>
      <textarea
        v-model="sku"
        class="spec-textarea"
        rows="5"
        placeholder="例如：F110G7 - Intel Core i5-1335U, 16GB RAM, 512GB PCIe SSD, WIFI + BT + 4G LTE w/ GPS, LED Barcode Reader, HF RFID, 3 Year B2B Warranty"
        :disabled="loading"></textarea>

      <div class="spec-actions">
        <button class="btn-primary" :disabled="loading || !sku.trim()" @click="generate">
          <span v-if="loading && mode === 'generate'">比對中…</span>
          <span v-else>比對規格</span>
        </button>
        <button class="btn-secondary" :disabled="loading || !sku.trim()" @click="downloadExcel">
          <span v-if="loading && mode === 'excel'">產生中…</span>
          <span v-else>下載 Excel 規格表</span>
        </button>
      </div>
    </div>

    <!-- Error / ambiguous-model prompt -->
    <div v-if="errorMsg" class="spec-error">
      <strong>⚠️ {{ errorMsg }}</strong>
      <div v-if="candidates && candidates.length" class="spec-candidates">
        <span>請選擇型號：</span>
        <button v-for="c in candidates" :key="c" class="chip" @click="pickCandidate(c)">{{ c }}</button>
      </div>
    </div>

    <!-- Agent (LLM) free-text answer -->
    <div v-if="agentText" class="spec-result-card">
      <div class="spec-meta">
        <span class="route-badge agent">🤖 AI 智慧</span>
        <span class="meta-file">此問題由 LLM 代理回答</span>
      </div>
      <div class="spec-markdown" v-html="safeHtml(mdToHtml(agentText))"></div>
    </div>

    <!-- Spec sheet result -->
    <div v-if="result.ok" class="spec-result-card">
      <div class="spec-meta">
        <span v-if="routeMode === 'recommend'" class="route-badge agent">🤖 AI 推薦</span>
        <span v-else-if="routeMode === 'agent'" class="route-badge agent">🤖 AI 智慧</span>
        <span v-else class="route-badge deterministic">⚡ 確定性</span>
        <span v-if="result.model" class="meta-badge model">{{ result.model }}</span>
        <span v-if="result.spec_file" class="meta-file">來源：{{ result.spec_file }}</span>
        <button v-if="routeMode !== 'recommend'" class="btn-link" @click="downloadExcel">⬇ 下載 Excel</button>
      </div>
      <div class="spec-markdown" v-html="renderedHtml"></div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'ProductSpecGenerator',
    data() {
      return {
        sku: '',
        loading: false,
        mode: null,            // request in flight: 'generate' | 'excel'
        routeMode: null,       // path taken by backend: 'deterministic' | 'agent'
        result: { ok: false, model: null, spec_file: null, markdown: '' },
        agentText: '',         // free-text answer when the LLM agent path runs
        errorMsg: '',
        candidates: [],
        examples: [
          {
            tag: '⚡ 規格',
            kind: 'deterministic',
            label: 'SKU → 完整規格表 (F110G7)',
            prompt: 'F110G7 - Intel Core i5-1335U Processor, 11.6" With Webcam, '
              + 'Microsoft Windows 11 Pro x64 with 16GB RAM + TAA, 512GB PCIe SSD, '
              + 'Sunlight Readable (Full HD LCD+ Touchscreen+Hard Tip stylus), '
              + 'DC Power Jack + AC Adapter + US Power Cord, Rear Camera + Tablet '
              + 'Hard Handle + Standard Batteries (2-pack), WIFI + BT + 4G LTE w/ '
              + 'integrated GPS/Glonass, LED Barcode Reader, HF RFID, 3 Year B2B Warranty',
          },
          {
            tag: '🤖 推薦',
            kind: 'agent',
            label: '使用情境 → 推薦產品 + 規格',
            prompt: 'recommend a rugged tablet for warehouse scanning',
          },
        ],
      };
    },
    computed: {
      renderedHtml() {
        return this.safeHtml(this.mdToHtml(this.result.markdown || ''));
      },
    },
    methods: {
      prdEnv() {
        return 'PRD' === this.$session.get('mode');
      },
      apiParams() {
        return {
          token: encodeURI(this.$store.state.token),
          username: encodeURI(this.$store.state.username),
        };
      },

      async generate() {
        const self = this;
        if (!this.sku.trim()) return;
        this.loading = true;
        this.mode = 'generate';
        this.routeMode = null;
        this.errorMsg = '';
        this.candidates = [];
        this.agentText = '';
        this.result = { ok: false, model: null, spec_file: null, markdown: '' };

        // Auto-routing endpoint: tries the deterministic table first and only
        // escalates to the LLM agent when the input reads like a question.
        const url = self.$root.baseUrl + '/genova-graph/api/product-spec/smart/';
        const body = { sku_description: this.sku.trim() };

        self.$axios.post(url, body, { params: self.apiParams() })
          .then((response) => {
            const res = response.data || {};
            self.routeMode = res.mode || null;

            if (res.mode === 'ambiguous') {
              self.errorMsg = res.message || res.description || '請選擇型號。';
              self.candidates = res.candidates || [];
              return;
            }
            if (res.mode === 'recommend') {
              // Web-search + LLM recommendation: combined markdown holds 1-2 full specs + why.
              if (res.ok && res.markdown) {
                self.result = { ok: true, model: null, spec_file: null, markdown: res.markdown };
              } else {
                self.errorMsg = res.message || res.description || '無法產生推薦。';
              }
              return;
            }
            if (res.mode === 'agent') {
              // The LLM ran. If it produced a structured spec, show it; else show text.
              if (res.markdown) {
                self.result = { ok: true, model: res.model, spec_file: res.spec_file, markdown: res.markdown };
              } else {
                self.agentText = res.response || '（AI 無回應）';
              }
              return;
            }
            // deterministic
            if (res.ok) {
              self.result = {
                ok: true, model: res.model, spec_file: res.spec_file, markdown: res.markdown || '',
              };
            } else {
              self.errorMsg = res.message || res.description || '無法比對到對應型號。';
              self.candidates = res.candidates || [];
            }
          })
          .catch((err) => {
            console.error(err);
            self.errorMsg = '伺服器錯誤，請稍後再試。';
          })
          .finally(() => { self.loading = false; self.mode = null; });
      },

      downloadExcel() {
        const self = this;
        if (!this.sku.trim()) return;
        this.loading = true;
        this.mode = 'excel';
        this.errorMsg = '';

        const url = self.$root.baseUrl + '/genova-graph/api/product-spec/excel/';
        const body = { sku_description: this.sku.trim() };

        self.$axios.post(url, body, { params: self.apiParams(), responseType: 'blob' })
          .then((response) => {
            const ctype = (response.headers['content-type'] || '');
            // The endpoint returns JSON (not a spreadsheet) when the model is
            // ambiguous/unmatched — read the blob back as text and surface it.
            if (ctype.indexOf('spreadsheet') === -1) {
              return response.data.text().then((txt) => {
                try {
                  const j = JSON.parse(txt);
                  self.errorMsg = j.description || j.message || '無法產生 Excel。';
                  self.candidates = j.candidates || [];
                } catch (e) {
                  self.errorMsg = '無法產生 Excel。';
                }
              });
            }
            // Trigger the file download.
            let filename = 'product_spec.xlsx';
            const cd = response.headers['content-disposition'] || '';
            const m = cd.match(/filename="?([^"]+)"?/);
            if (m) filename = m[1];

            const blob = new Blob([response.data], { type: ctype });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
          })
          .catch((err) => {
            console.error(err);
            self.errorMsg = '伺服器錯誤，無法產生 Excel。';
          })
          .finally(() => { self.loading = false; self.mode = null; });
      },

      runExample(ex) {
        // Fill the input with the example prompt and run it immediately.
        this.sku = ex.prompt;
        this.generate();
      },

      pickCandidate(model) {
        // Prepend the chosen model so matching resolves unambiguously.
        this.sku = model + ' ' + this.sku;
        this.errorMsg = '';
        this.candidates = [];
        this.generate();
      },

      // Minimal Markdown → HTML for the fixed-shape spec sheet
      // (headings, bold, bullets, hr). Output is sanitised via safeHtml.
      mdToHtml(md) {
        const esc = (s) => s
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const inline = (s) => esc(s)
          .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
          .replace(/\*([^*]+)\*/g, '<em>$1</em>');

        const lines = (md || '').split(/\r?\n/);
        let html = '';
        let inList = false;
        const closeList = () => { if (inList) { html += '</ul>'; inList = false; } };

        for (const raw of lines) {
          const line = raw.replace(/\s+$/, '');
          if (/^---\s*$/.test(line)) { closeList(); html += '<hr/>'; continue; }
          if (/^###\s+/.test(line)) { closeList(); html += '<h4>' + inline(line.replace(/^###\s+/, '')) + '</h4>'; continue; }
          if (/^##\s+/.test(line)) { closeList(); html += '<h3>' + inline(line.replace(/^##\s+/, '')) + '</h3>'; continue; }
          if (/^#\s+/.test(line)) { closeList(); html += '<h2>' + inline(line.replace(/^#\s+/, '')) + '</h2>'; continue; }
          const bullet = line.match(/^(\s*)-\s+(.*)$/);
          if (bullet) {
            if (!inList) { html += '<ul>'; inList = true; }
            const indent = bullet[1].length >= 2 ? ' class="sub"' : '';
            html += '<li' + indent + '>' + inline(bullet[2]) + '</li>';
            continue;
          }
          closeList();
          if (line.trim() === '') { continue; }
          html += '<p>' + inline(line) + '</p>';
        }
        closeList();
        return html;
      },
    },
  };
</script>

<style scoped>
  .spec-gen {
    max-width: 980px;
    margin: 0 auto;
    padding: 24px 20px 60px;
    color: #2c3e50;
  }

  .spec-header h2 {
    margin: 0 0 4px;
    color: #2c3e6b;
    font-weight: 700;
  }

  .spec-sub {
    color: #6b7280;
    margin: 0 0 18px;
    font-size: 14px;
  }

  .spec-examples {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
  }

  .ex-title {
    font-size: 13px;
    font-weight: 600;
    color: #4a5568;
  }

  .ex-card {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: 340px;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 10px;
    padding: 8px 12px;
    cursor: pointer;
    text-align: left;
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  }

    .ex-card:hover:not(:disabled) {
      border-color: #2c5aba;
      box-shadow: 0 2px 8px rgba(44, 90, 186, 0.12);
      background: #f8fbff;
    }

    .ex-card:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

  .ex-tag {
    font-size: 11.5px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 12px;
    white-space: nowrap;
  }

    .ex-tag.deterministic {
      background: #d1fadf;
      color: #1a7f3c;
    }

    .ex-tag.agent {
      background: #e0e7ff;
      color: #3730a3;
    }

  .ex-label {
    font-size: 13px;
    color: #2c3e50;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .spec-input-card {
    background: #fff;
    border: 1px solid #dde3ed;
    border-radius: 10px;
    padding: 18px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  .spec-label {
    display: block;
    font-weight: 600;
    font-size: 13px;
    color: #4a5568;
    margin-bottom: 6px;
  }

  .spec-textarea {
    width: 100%;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 12px;
    font-size: 14px;
    font-family: inherit;
    resize: vertical;
    box-sizing: border-box;
  }

    .spec-textarea:focus {
      outline: none;
      border-color: #2c5aba;
      box-shadow: 0 0 0 3px rgba(44, 90, 186, 0.12);
    }

  .spec-actions {
    display: flex;
    gap: 10px;
    margin-top: 12px;
  }

  .btn-primary,
  .btn-secondary {
    border: none;
    border-radius: 8px;
    padding: 9px 18px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, opacity 0.15s;
  }

  .btn-primary {
    background: #2c5aba;
    color: #fff;
  }

    .btn-primary:hover:not(:disabled) { background: #1f4596; }

  .btn-secondary {
    background: #e8f0fe;
    color: #2c5aba;
  }

    .btn-secondary:hover:not(:disabled) { background: #d8e6fd; }

  .btn-primary:disabled,
  .btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spec-error {
    margin-top: 16px;
    padding: 12px 16px;
    background: #fff4f4;
    border: 1px solid #f5c2c2;
    border-radius: 8px;
    color: #b91c1c;
  }

  .spec-candidates {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    color: #4a5568;
  }

  .chip {
    background: #eef2ff;
    border: 1px solid #c7d2fe;
    color: #3730a3;
    border-radius: 16px;
    padding: 3px 12px;
    font-size: 12.5px;
    cursor: pointer;
  }

    .chip:hover { background: #e0e7ff; }

  .spec-result-card {
    margin-top: 18px;
    background: #fff;
    border: 1px solid #dde3ed;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  .spec-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #f4f7fb;
    border-bottom: 1px solid #dde3ed;
  }

  .meta-badge.model {
    background: #2c3e6b;
    color: #fff;
    font-weight: 700;
    padding: 3px 12px;
    border-radius: 6px;
    font-family: monospace;
  }

  .route-badge {
    font-size: 12px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 20px;
  }

  .route-badge.deterministic {
    background: #d1fadf;
    color: #1a7f3c;
  }

  .route-badge.agent {
    background: #e0e7ff;
    color: #3730a3;
  }

  .meta-file {
    color: #6b7280;
    font-size: 12.5px;
  }

  .btn-link {
    margin-left: auto;
    background: none;
    border: none;
    color: #2c5aba;
    font-weight: 600;
    cursor: pointer;
    font-size: 13px;
  }

    .btn-link:hover { text-decoration: underline; }

  .spec-markdown {
    padding: 8px 22px 22px;
    line-height: 1.6;
    font-size: 14px;
  }

    .spec-markdown >>> h2 {
      color: #2c3e6b;
      border-bottom: 2px solid #e0eaf7;
      padding-bottom: 6px;
      margin-top: 18px;
    }

    .spec-markdown >>> h3 {
      color: #2c5aba;
      margin-top: 18px;
      margin-bottom: 6px;
    }

    .spec-markdown >>> h4 {
      color: #475569;
      margin: 12px 0 4px;
    }

    .spec-markdown >>> ul {
      margin: 4px 0 10px;
      padding-left: 22px;
    }

    .spec-markdown >>> li { margin: 2px 0; }

    .spec-markdown >>> li.sub {
      list-style: circle;
      margin-left: 18px;
      color: #5a6375;
    }

    .spec-markdown >>> hr {
      border: none;
      border-top: 1px dashed #cbd5e1;
      margin: 16px 0;
    }
</style>



// WEBPACK FOOTER //
// src/components/framework-gen-ai/ProductSpecGenerator.vue